import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type AuthUser = {
  id: string;
  email: string;
  displayName: string;
};

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

type AuthState = {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  setSession: (user: AuthUser, tokens: AuthTokens) => void;
  setTokens: (tokens: AuthTokens) => void;
  /**
   * Patches the cached user. Used after PATCH /api/auth/me
   * succeeds so the header re-renders with the new displayName
   * without a full session reset.
   */
  patchUser: (patch: Partial<AuthUser>) => void;
  clear: () => void;
};

/**
 * Auth state backed by localStorage via zustand's persist middleware.
 *
 * Security note: storing JWTs in localStorage is convenient but
 * susceptible to XSS. The production pattern is httpOnly cookies set
 * by the API. This is acceptable for the current dev scaffold; a
 * future PR will migrate auth to cookie-based sessions and drop this
 * persistence path.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tokens: null,
      setSession: (user, tokens) => set({ user, tokens }),
      setTokens: (tokens) => set((state) => ({ ...state, tokens })),
      patchUser: (patch) =>
        set((state) => (state.user ? { ...state, user: { ...state.user, ...patch } } : state)),
      clear: () => set({ user: null, tokens: null }),
    }),
    {
      name: 'ecg.auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
